import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc,
  updateDoc,
  deleteDoc,
  query, 
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { ShipFromAddress } from '../types';

const COLLECTION_NAME = 'shipFromAddresses';

/**
 * Get all ship-from addresses
 */
export const getShipFromAddresses = async (): Promise<ShipFromAddress[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('isDefault', 'desc'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    })) as ShipFromAddress[];
  } catch (error) {
    console.error('Error fetching ship-from addresses:', error);
    throw new Error('Failed to fetch ship-from addresses');
  }
};

/**
 * Get default ship-from address
 */
export const getDefaultShipFromAddress = async (): Promise<ShipFromAddress | null> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('isDefault', '==', true)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    } as ShipFromAddress;
  } catch (error) {
    console.error('Error fetching default ship-from address:', error);
    throw new Error('Failed to fetch default ship-from address');
  }
};

/**
 * Create a new ship-from address
 */
export const createShipFromAddress = async (addressData: Omit<ShipFromAddress, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    // If this is being set as default, unset other defaults first
    if (addressData.isDefault) {
      await unsetAllDefaults();
    }
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...addressData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating ship-from address:', error);
    throw new Error('Failed to create ship-from address');
  }
};

/**
 * Update an existing ship-from address
 */
export const updateShipFromAddress = async (id: string, updates: Partial<ShipFromAddress>): Promise<void> => {
  try {
    // If this is being set as default, unset other defaults first
    if (updates.isDefault) {
      await unsetAllDefaults();
    }
    
    const docRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating ship-from address:', error);
    throw new Error('Failed to update ship-from address');
  }
};

/**
 * Delete a ship-from address
 */
export const deleteShipFromAddress = async (id: string): Promise<void> => {
  try {
    // Check if this is the default address
    const addressDoc = await getDoc(doc(db, COLLECTION_NAME, id));
    if (!addressDoc.exists()) {
      throw new Error('Address not found');
    }
    
    const addressData = addressDoc.data() as ShipFromAddress;
    
    // Don't allow deletion of default address if it's the only one
    if (addressData.isDefault) {
      const allAddresses = await getShipFromAddresses();
      if (allAddresses.length === 1) {
        throw new Error('Cannot delete the only ship-from address');
      }
      
      // If there are other addresses, set the first non-default as default
      const otherAddresses = allAddresses.filter(addr => addr.id !== id);
      if (otherAddresses.length > 0) {
        await updateShipFromAddress(otherAddresses[0].id!, { isDefault: true });
      }
    }
    
    await deleteDoc(doc(db, COLLECTION_NAME, id));
  } catch (error) {
    console.error('Error deleting ship-from address:', error);
    throw new Error('Failed to delete ship-from address');
  }
};

/**
 * Set an address as default
 */
export const setDefaultShipFromAddress = async (id: string): Promise<void> => {
  try {
    // Unset all defaults first
    await unsetAllDefaults();
    
    // Set the selected address as default
    await updateShipFromAddress(id, { isDefault: true });
  } catch (error) {
    console.error('Error setting default ship-from address:', error);
    throw new Error('Failed to set default ship-from address');
  }
};

/**
 * Helper function to unset all default flags
 */
const unsetAllDefaults = async (): Promise<void> => {
  const addresses = await getShipFromAddresses();
  const defaultAddresses = addresses.filter(addr => addr.isDefault);
  
  const updatePromises = defaultAddresses.map(addr => 
    updateDoc(doc(db, COLLECTION_NAME, addr.id!), {
      isDefault: false,
      updatedAt: serverTimestamp(),
    })
  );
  
  await Promise.all(updatePromises);
};

/**
 * Validate ship-from address data
 */
export const validateShipFromAddress = (address: Partial<ShipFromAddress>): string[] => {
  const errors: string[] = [];
  
  if (!address.name?.trim()) {
    errors.push('Name is required');
  }
  
  if (!address.company?.trim()) {
    errors.push('Company name is required');
  }
  
  if (!address.line1?.trim()) {
    errors.push('Address line 1 is required');
  }
  
  if (!address.city?.trim()) {
    errors.push('City is required');
  }
  
  if (!address.state?.trim()) {
    errors.push('State/Province is required');
  }
  
  if (!address.zipCode?.trim()) {
    errors.push('ZIP/Postal code is required');
  }
  
  if (!address.country?.trim()) {
    errors.push('Country is required');
  }
  
  if (!address.phone?.trim()) {
    errors.push('Phone number is required');
  }
  
  if (!address.customsSigner?.trim()) {
    errors.push('Customs signer name is required');
  }
  
  // Validate phone number format (basic)
  if (address.phone && !/^\+?[\d\s\-\(\)]+$/.test(address.phone)) {
    errors.push('Phone number format is invalid');
  }
  
  // Validate email format if provided
  if (address.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address.email)) {
    errors.push('Email format is invalid');
  }
  
  // Validate country code (should be 2 characters)
  if (address.country && address.country.length !== 2) {
    errors.push('Country should be a 2-character code (e.g., US, CA, JM)');
  }
  
  return errors;
};